// F12.4 — line-level diff (LCS-based).
//
// Tiny in-house implementation. No `diff` npm dep — the algorithm is small and
// keeps the bundle lean. Produces an op-stream the DiffView walks to render a
// side-by-side view, and `copyAsGist` formats a snippet as a Markdown fenced
// block for clipboard.
//
// Algorithm: classic O(m·n) DP for longest common subsequence over `a`/`b`,
// then back-track to emit `{op,text}` items in input order. Suitable for the
// small (< 5000 line) snippets the Code Drop pack handles; if huge inputs ever
// land, swap in Myers' O(N + D²).
//
// Token semantics:
//   - 'eq'  — line present in both sides
//   - 'add' — line in `b` only (the "target" snippet)
//   - 'del' — line in `a` only (the "source" snippet)

/**
 * @typedef {Object} DiffLine
 * @property {'eq'|'add'|'del'} op
 * @property {string} text
 * @property {number} [leftLine]   1-based line number in `a` for 'eq'/'del'
 * @property {number} [rightLine]  1-based line number in `b` for 'eq'/'add'
 */

/**
 * Diff two arrays of lines.
 *
 * @param {string[]} a  source lines
 * @param {string[]} b  target lines
 * @returns {DiffLine[]}
 */
export function diffLines(a, b) {
  const m = a.length;
  const n = b.length;

  // dp[i][j] = LCS length of a[i:], b[j:].
  // Allocate once; small bubble allocations would dominate.
  const dp = new Array(m + 1);
  for (let i = 0; i <= m; i++) dp[i] = new Int32Array(n + 1);

  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      if (a[i] === b[j]) dp[i][j] = dp[i + 1][j + 1] + 1;
      else dp[i][j] = dp[i + 1][j] >= dp[i][j + 1] ? dp[i + 1][j] : dp[i][j + 1];
    }
  }

  const out = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      out.push({ op: 'eq', text: a[i], leftLine: i + 1, rightLine: j + 1 });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ op: 'del', text: a[i], leftLine: i + 1 });
      i++;
    } else {
      out.push({ op: 'add', text: b[j], rightLine: j + 1 });
      j++;
    }
  }
  while (i < m) out.push({ op: 'del', text: a[i], leftLine: i + 1 }), i++;
  while (j < n) out.push({ op: 'add', text: b[j], rightLine: j + 1 }), j++;
  return out;
}

/**
 * Diff two snippet bodies (newline-delimited strings).
 *
 * @param {string} aText
 * @param {string} bText
 * @returns {DiffLine[]}
 */
export function diffText(aText, bText) {
  // Use \n consistently. Strip a trailing \n to avoid a phantom empty-string
  // line at the end (paste from many editors includes one).
  const norm = (s) => (s.endsWith('\n') ? s.slice(0, -1) : s);
  return diffLines(norm(aText).split('\n'), norm(bText).split('\n'));
}

/**
 * Quick stats over a diff stream — useful for the DiffView footer.
 *
 * @param {DiffLine[]} diff
 * @returns {{ added: number, removed: number, unchanged: number }}
 */
export function diffStats(diff) {
  let added = 0;
  let removed = 0;
  let unchanged = 0;
  for (const line of diff) {
    if (line.op === 'add') added++;
    else if (line.op === 'del') removed++;
    else unchanged++;
  }
  return { added, removed, unchanged };
}

/**
 * Format a snippet as a GitHub-Gist-style Markdown fenced block.
 * Caller passes the result to `navigator.clipboard.writeText`.
 *
 * @param {{ lang?: string, text: string }} snippet
 * @returns {string}
 */
export function asGistMarkdown({ lang, text }) {
  const body = text.endsWith('\n') ? text : text + '\n';
  const fenceLang = (lang || '').replace(/[^a-z0-9_+-]/gi, '').slice(0, 20);
  return `\`\`\`${fenceLang}\n${body}\`\`\`\n`;
}
